import React, { useEffect, useRef, useState } from 'react';
import sortedProjects from '../data/projects';
import { educationItems, professionalItems } from '../data/timeline';

const Terminal = () => {
  const [lines, setLines] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(null);
  const [sideView, setSideView] = useState(null); // e.g. 'projects'

  const outputRef = useRef(null);
  const inputRef = useRef(null);

  const RESUME_URLS = {
    fr: '/cv/CV-Saad Beidouri- FR.pdf',
    en: '/cv/CV-Saad Beidouri- EN.pdf',
  };

  const CONTACT = {
    email: 'saad.beidouri.pro@gmail.com',
    github: 'https://github.com/SaadBeidourii',
    linkedinLabel: 'https://www.linkedin.com/in/saad-beidouri/',
    linkedinUrl: 'https://www.linkedin.com/in/saad-beidouri-23b6481b4/',
  };

  const SKILLS = {
    languages: ['Java', 'TypeScript', 'JavaScript', 'Go', 'Python'],
    frameworks: ['React', 'Angular', 'Node.js', 'Express', 'Spring Boot'],
    tools: ['Docker', 'Git', 'Linux', 'PostgreSQL', 'MongoDB'],
  };

  const FUN_MESSAGES = [
    "It works on my machine.",
  ];

  useEffect(() => {
    const bootLines = [
      "Booting Saad's portfolio...",
      'Loading modules: about, projects, skills, contact, resume, timeline',
      "Type 'help' to see available commands.",
    ];

    setLines(bootLines.map((text) => ({ type: 'output', text })));
  }, []);

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [lines]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const print = (text, options = {}) => {
    setLines((prev) => [...prev, { type: 'output', text, ...options }]);
  };

  const printCommand = (commandText) => {
    setLines((prev) => [
      ...prev,
      { type: 'command', text: commandText },
    ]);
  };

  const handleHelp = () => {
    const helpLines = [
      'Available commands:',
      "  about      - About Saad",
      "  projects   - View selected projects",
      "  projects view  - Open projects panel on the right",
      "  skills     - Tech stack & tools",
      "  contact    - Email & social links",
      "  contact view - Open contact panel on the right",
      "  resume     - Show resume options (FR / EN)",
      "  timeline   - Education & professional experience",
      "  timeline view  - Open timeline panel on the right",
      "  view off       - Close the right-side panel",
      "  clear      - Clear the screen",
      "  help       - Show this help message",
    ];
    helpLines.forEach((line) => print(line));
  };

  const handleAbout = () => {
    const aboutLines = [
      '┌─ About Saad Beidouri ───────────────────────────────────────────┐',
      '',
      '  Role',
      '  Full Stack Engineer at ProBTP / Abylsen (since September 2025).',
      '  Designing and developing monitoring interfaces, backend services,',
      '  and platform reliability tooling within the same team as his',
      '  apprenticeship, now as an external engineer through Abylsen.',
      '',
      '  Education',
      '  Engineering degree in Software Engineering — Polytech Nice Sophia',
      '  (2025), specialising in IHM and UX/UI design. Preceded by two',
      '  years of CPGE (MPSI → MP) at Lycée François Rabelais, Saint-Brieuc,',
      '  admitted to Polytech with honours.',
      '',
      '  Core Stack',
      '  Java · Spring Boot · TypeScript · React · Next.js · Angular',
      '  Go · Python · Docker · PostgreSQL · MongoDB',
      '  Grafana · Prometheus · Loki',
      '',
      '  Notable Projects',
      '  • PlanBoard — Full-stack teacher dashboard (Spring Boot + Next.js).',
      '    JWT auth, grade tracking, student action plans, full search.',
      '  • Budgeteer — Android expense tracker for shared household costs.',
      '  • PS8 Quoridor — Real-time strategic board game with fog of war',
      '    and AI opponent, built with Socket.io.',
      '',
      '  What drives him',
      '  Building software that is clean, reliable, and genuinely useful —',
      '  with a strong focus on engineering quality and user experience.',
      '',
      '└────────────────────────────────────────────────────────────────────┘',
    ];
    aboutLines.forEach((line) => print(line));
  };

  const handleProjects = (args = []) => {
    if (!sortedProjects || sortedProjects.length === 0) {
      print('No projects available at the moment.');
      return;
    }

    print('Projects:');
    sortedProjects.forEach((project, index) => {
      const isInProgress = project.status && project.status.toLowerCase() === 'in progress';
      const statusEmoji = isInProgress ? ' 🚧' : '';
      const statusLabel = project.status ? ` [${project.status}]` : '';
      const baseLine = `${index + 1}. ${project.title}${statusLabel}${statusEmoji}`;

      print(baseLine);
      if (project.description) {
        print(`   - ${project.description}`);
      }
      if (project.githubLink) {
        print(`   > GitHub: ${project.githubLink}`);
      }
      print('');
    });

    const wantsView = args[0] && args[0].toLowerCase() === 'view';
    if (wantsView) {
      setSideView('projects');
    }
  };

  const handleSkills = () => {
    print('Skills:');
    if (SKILLS.languages?.length) {
      print(`  Languages: ${SKILLS.languages.join(', ')}`);
    }
    if (SKILLS.frameworks?.length) {
      print(`  Frameworks: ${SKILLS.frameworks.join(', ')}`);
    }
    if (SKILLS.tools?.length) {
      print(`  Tools: ${SKILLS.tools.join(', ')}`);
    }
  };

  const handleContact = (args = []) => {
    print('Contact:');
    if (CONTACT.email) {
      print(`  Email: ${CONTACT.email}`);
    }
    if (CONTACT.github) {
      print(`  GitHub: ${CONTACT.github}`);
    }
    if (CONTACT.linkedinLabel) {
      print(`  LinkedIn: ${CONTACT.linkedinLabel}`);
    }

    const wantsView = args[0] && args[0].toLowerCase() === 'view';
    if (wantsView) {
      setSideView('contact');
    }
  };

  const handleResume = (args = []) => {
    const langArg = (args[0] || '').toLowerCase();

    if (!langArg) {
      print('Usage: resume [fr|en]');
      print('  fr - Open French resume');
      print('  en - Open English resume');
      return;
    }

    if (langArg === 'fr' && RESUME_URLS.fr) {
      print('Opening French resume (FR)...');
      window.open(RESUME_URLS.fr, '_blank', 'noopener,noreferrer');
      return;
    }

    if (langArg === 'en' && RESUME_URLS.en) {
      print('Opening English resume (EN)...');
      window.open(RESUME_URLS.en, '_blank', 'noopener,noreferrer');
      return;
    }

    print(`Unknown option for resume: ${langArg}`, { isError: true });
    print("Use 'resume fr' or 'resume en'.");
  };

  const handleTimeline = (args = []) => {
    const hasEducation = educationItems && educationItems.length > 0;
    const hasProfessional = professionalItems && professionalItems.length > 0;

    if (!hasEducation && !hasProfessional) {
      print('No timeline data available.');
      return;
    }

    if (hasProfessional) {
      print('Professional Experience:');
      professionalItems.forEach((item, index) => {
        print(`  ${index + 1}. ${item.title} (${item.date})`);
        if (item.description) {
          print(`     - ${item.description}`);
        }
        print('');
      });
    }

    if (hasEducation) {
      print('Education:');
      educationItems.forEach((item, index) => {
        print(`  ${index + 1}. ${item.title} (${item.date})`);
        if (item.description) {
          print(`     - ${item.description}`);
        }
        print('');
      });
    }

    const wantsView = args[0] && args[0].toLowerCase() === 'view';
    if (wantsView) {
      setSideView('timeline');
    }
  };

  const handleClear = () => {
    setLines([]);
    setSideView(null);
  };

  const handleView = (args = []) => {
    const action = (args[0] || '').toLowerCase();

    if (!action) {
      if (!sideView) {
        print('View mode: off');
      } else {
        print(`View mode: ${sideView}`);
        print("Use 'view off' to close the right-side panel.");
      }
      return;
    }

    if (action === 'off' || action === 'close') {
      if (!sideView) {
        print('View mode is already off.');
      } else {
        setSideView(null);
        print('Closing side view panel.');
      }
      return;
    }

    print(`Unknown view action: ${action}`, { isError: true });
    print("Use 'view off' to close the panel.");
  };

  const handleEasterEggs = (command, args) => {
    if (command === 'sudo') {
      print("Nice try, but you don't have sudo rights on this machine.");
      return true;
    }

    if (command === 'whoami') {
      print('Saad Beidouri, Software Engineer.');
      return true;
    }

    if (command === 'ls') {
      print('about.txt  projects/  skills.txt  contact.sh  resume.pdf  timeline/');
      return true;
    }

    if (command === 'hello' || command === 'hi') {
      print('Hello there! Type "help" to explore this portfolio.');
      return true;
    }

    if (command === 'saad') {
      print('saad: command recognized. Result: Full-stack engineer with a taste for clean UIs and reliable backends.');
      return true;
    }

    if (command === 'beidouri') {
      print('beidouri: legendary namespace detected. Access granted to portfolio secrets (try `projects view` or `timeline view`).');
      return true;
    }

    if (command === 'cls') {
      handleClear();
      return true;
    }

    if (command === 'rm' && args[0] === '-rf' && (args[1] === '/' || args[1] === '/*')) {
      print('System integrity check: denied. Your portfolio is safe.');
      return true;
    }

    return false;
  };

  const handleCommand = (rawInput) => {
    const input = rawInput.trim();
    if (!input) return;

    printCommand(input);
    setHistory((prev) => [...prev, input]);
    setHistoryIndex(null);

    const [command, ...args] = input.split(/\s+/);
    const normalized = command.toLowerCase();

    if (handleEasterEggs(normalized, args)) {
      return;
    }

    switch (normalized) {
      case 'help':
        handleHelp();
        break;
      case 'about':
        handleAbout();
        break;
      case 'projects':
        handleProjects(args);
        break;
      case 'skills':
        handleSkills();
        break;
      case 'contact':
        handleContact(args);
        break;
      case 'resume':
        handleResume(args);
        break;
      case 'timeline':
        handleTimeline(args);
        break;
      case 'view':
        handleView(args);
        break;
      case 'clear':
        handleClear();
        break;
      default:
        print(`command not found: ${command}`, { isError: true });
        print("Type 'help' to see available commands.");
        break;
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const value = inputValue;
      setInputValue('');
      handleCommand(value);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!history.length) return;
      setHistoryIndex((current) => {
        const nextIndex = current === null ? history.length - 1 : Math.max(0, current - 1);
        setInputValue(history[nextIndex]);
        return nextIndex;
      });
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (historyIndex === null) return;
      setHistoryIndex((current) => {
        if (current === null) return null;
        const nextIndex = current + 1;
        if (nextIndex >= history.length) {
          setInputValue('');
          return null;
        }
        setInputValue(history[nextIndex]);
        return nextIndex;
      });
    }
  };

  const handleTerminalClick = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div className="terminal-root" onClick={handleTerminalClick}>
      <div className={sideView ? 'terminal-layout terminal-layout-split' : 'terminal-layout'}>
        <div className="terminal-window">
          <div className="terminal-header">
            <div className="terminal-buttons">
              <span className="btn close" />
              <span className="btn minimize" />
              <span className="btn maximize" />
            </div>
            <div className="terminal-title">saad@portfolio: ~</div>
          </div>

          <div
            className="terminal-body"
            id="terminal-output"
            ref={outputRef}
            aria-live="polite"
          >
            {lines.map((line, index) => (
              <div
                key={index}
                className={`terminal-line ${
                  line.type === 'command'
                    ? 'terminal-line-command'
                    : line.isError
                    ? 'terminal-line-error'
                    : ''
                }`}
              >
                {line.type === 'command' ? (
                  <>
                    <span className="terminal-prompt">saad@portfolio:~$ </span>
                    <span>{line.text}</span>
                  </>
                ) : (
                  <span>{line.text}</span>
                )}
              </div>
            ))}
          </div>

          <div className="terminal-input-line">
            <span className="terminal-prompt">saad@portfolio:~$</span>
            <div className="terminal-input-wrapper">
              <input
                ref={inputRef}
                id="terminal-input"
                type="text"
                autoComplete="off"
                spellCheck="false"
                aria-label="Terminal command input"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <span className="terminal-cursor" />
            </div>
          </div>
        </div>

        {sideView === 'projects' && (
          <div className="terminal-side-panel">
            <h2 className="side-panel-title">Projects</h2>
            <div className="side-panel-content side-panel-projects">
              {sortedProjects.map((project, index) => {
                const isInProgress = project.status && project.status.toLowerCase() === 'in progress';
                const statusEmoji = isInProgress ? '🚧' : '';

                return (
                  <div key={index} className="project-card">
                    <div className="project-card-header">
                      <h3 className="project-card-title">{project.title}</h3>
                      {project.status && (
                        <span className="project-card-status">
                          {project.status} {statusEmoji}
                        </span>
                      )}
                    </div>
                    {project.description && (
                      <p className="project-card-description">{project.description}</p>
                    )}
                    {project.githubLink && (
                      <a
                        href={project.githubLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="project-card-link"
                      >
                        View on GitHub
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {sideView === 'timeline' && (
          <div className="terminal-side-panel">
            <h2 className="side-panel-title">Timeline</h2>
            <div className="side-panel-content side-panel-timeline">
              {professionalItems && professionalItems.length > 0 && (
                <>
                  <h3 className="timeline-section-title">Professional Experience</h3>
                  {professionalItems.map((item, index) => (
                    <div key={`pro-${index}`} className="timeline-card">
                      <div className="timeline-card-header">
                        <h4 className="timeline-card-title">{item.title}</h4>
                        {item.tag && <span className="timeline-card-tag">{item.tag}</span>}
                      </div>
                      <p className="timeline-card-date">{item.date}</p>
                      {item.description && (
                        <p className="timeline-card-description">{item.description}</p>
                      )}
                    </div>
                  ))}
                </>
              )}

              {educationItems && educationItems.length > 0 && (
                <>
                  <h3 className="timeline-section-title">Education</h3>
                  {educationItems.map((item, index) => (
                    <div key={`edu-${index}`} className="timeline-card">
                      <div className="timeline-card-header">
                        <h4 className="timeline-card-title">{item.title}</h4>
                        {item.tag && <span className="timeline-card-tag">{item.tag}</span>}
                      </div>
                      <p className="timeline-card-date">{item.date}</p>
                      {item.description && (
                        <p className="timeline-card-description">{item.description}</p>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        )}

        {sideView === 'contact' && (
          <div className="terminal-side-panel">
            <h2 className="side-panel-title">Contact</h2>
            <div className="side-panel-content side-panel-contact">
              {CONTACT.email && (
                <div className="contact-row">
                  <div className="contact-label-with-icon">
                    <span className="contact-icon">📧</span>
                    <span className="contact-label">Email</span>
                  </div>
                  <a href={`mailto:${CONTACT.email}`} className="contact-value">
                    {CONTACT.email}
                  </a>
                </div>
              )}
              {CONTACT.github && (
                <div className="contact-row">
                  <div className="contact-label-with-icon">
                    <span className="contact-icon">🐙</span>
                    <span className="contact-label">GitHub</span>
                  </div>
                  <a
                    href={CONTACT.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-value"
                  >
                    {CONTACT.github}
                  </a>
                </div>
              )}
              {CONTACT.linkedinLabel && CONTACT.linkedinUrl && (
                <div className="contact-row">
                  <div className="contact-label-with-icon">
                    <span className="contact-icon">💼</span>
                    <span className="contact-label">LinkedIn</span>
                  </div>
                  <a
                    href={CONTACT.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-value"
                  >
                    {CONTACT.linkedinLabel}
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Terminal;
